// apps/frontend/src/lib/pipeline-preview/executor.ts
import type { PipelineNode, PipelineEdge } from '@/pages/pipelines/types/pipeline';
import type { ExecutionContext, TransformerResult } from './types';
import { buildExecutionPath, findParentNodes } from './graph-utils';
import { sourceData } from './transformers/source';
import { transformData } from './transformers/transformation';
import { filterData } from './transformers/filter';
import { aggregateData } from './transformers/aggregation';
import { joinData } from './transformers/join';
import { destinationData } from './transformers/destination';
import { inferSchema } from './utils/schema';

/**
 * Transformer function type - matches actual implementation signatures
 */
type TransformerFn = (
  node: PipelineNode,
  inputData: unknown[],
  context: ExecutionContext
) => Promise<TransformerResult>;

/**
 * Pipeline Preview Executor
 * Executes transformation pipeline in browser for preview purposes
 */
export class PipelinePreviewExecutor {
  private nodes: PipelineNode[];
  private edges: PipelineEdge[];
  private context: ExecutionContext;

  constructor(nodes: PipelineNode[], edges: PipelineEdge[]) {
    this.nodes = nodes;
    this.edges = edges;
    this.context = {
      nodes,
      edges,
      dataCache: new Map(),
    };
  }

  /**
   * Execute pipeline up to target node and return preview data
   */
  async execute(targetNodeId: string): Promise<TransformerResult> {
    try {
      // Find target node
      const targetNode = this.nodes.find(n => n.id === targetNodeId);
      if (!targetNode) {
        return {
          success: false,
          data: [],
          schema: { columns: [] },
          stats: { rows: 0, executionTime: 0 },
          error: `Node ${targetNodeId} not found`,
        };
      }

      // Build execution path
      const path = buildExecutionPath(this.nodes, this.edges, targetNodeId);
      if (!path.isValid) {
        return {
          success: false,
          data: [],
          schema: { columns: [] },
          stats: { rows: 0, executionTime: 0 },
          error: path.error || 'Invalid execution path',
        };
      }

      // Execute path sequentially
      let currentData: unknown[] = [];

      for (const node of path.nodes) {
        const result = await this.executeNode(node, currentData);

        if (!result.success) {
          return result;
        }

        currentData = result.data;
      }

      // Return final result
      return {
        success: true,
        data: currentData,
        schema: inferSchema(currentData),
        stats: {
          rows: currentData.length,
          executionTime: 0, // Total time would require summing
        },
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: error.message || 'Execution failed',
      };
    }
  }

  /**
   * Execute a single node
   */
  private async executeNode(
    node: PipelineNode,
    inputData: unknown[]
  ): Promise<TransformerResult> {
    const transformer = this.getTransformer(node.data.type);

    if (!transformer) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: `No transformer found for node type: ${node.data.type}`,
      };
    }

    // Special handling for JOIN (needs two inputs)
    // Note: Cycle detection is handled by buildExecutionPath during path construction
    if (node.data.type === 'JOIN') {
      return await this.executeJoinNode(node);
    }

    return await transformer(node, inputData, this.context);
  }

  /**
   * Execute JOIN node (requires two parent nodes)
   *
   * Note: buildExecutionPath returns a linear path which may only include
   * one branch of the JOIN. We handle this by recursively executing both
   * parent branches independently, each building its own path to SOURCE.
   * This ensures both sides of the JOIN are processed correctly even though
   * the initial path only traces back through one branch.
   */
  private async executeJoinNode(node: PipelineNode): Promise<TransformerResult> {
    const parents = findParentNodes(this.nodes, this.edges, node.id);

    if (parents.length !== 2) {
      return {
        success: false,
        data: [],
        schema: { columns: [] },
        stats: { rows: 0, executionTime: 0 },
        error: 'JOIN node requires exactly two input nodes',
      };
    }

    // Execute both parent branches
    const [leftResult, rightResult] = await Promise.all([
      this.execute(parents[0].id),
      this.execute(parents[1].id),
    ]);

    if (!leftResult.success) return leftResult;
    if (!rightResult.success) return rightResult;

    // Perform join
    return await joinData(node, [leftResult.data, rightResult.data], this.context);
  }

  /**
   * Get transformer function for node type
   */
  private getTransformer(nodeType: string): TransformerFn | null {
    switch (nodeType) {
      case 'SOURCE':
        return sourceData;
      case 'TRANSFORMATION':
        return transformData;
      case 'FILTER':
        return filterData;
      case 'AGGREGATION':
        return aggregateData;
      case 'JOIN':
        return joinData;
      case 'DESTINATION':
        return destinationData;
      default:
        return null;
    }
  }
}
